import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GpsDeviceStats {
  total: number;
  online: number;
  offline: number;
  moving: number;
  idle: number;
}

export function useGpsDeviceStats(companyId?: string) {
  return useQuery({
    queryKey: ['gps-device-stats', companyId],
    queryFn: async (): Promise<GpsDeviceStats> => {
      if (!companyId) {
        return {
          total: 0,
          online: 0,
          offline: 0,
          moving: 0,
          idle: 0,
        };
      }

      // Fetch all active devices with their latest positions
      const { data: devices, error } = await supabase
        .rpc('get_gps_devices_with_vehicles', { 
          p_company_id: companyId 
        });

      if (error) throw error;

      const now = new Date();
      const onlineThreshold = 5 * 60 * 1000; // 5 minutes

      let online = 0;
      let offline = 0;
      let moving = 0;
      let idle = 0;

      devices?.forEach((device: any) => {
        const lastSignal = device.last_signal_at ? new Date(device.last_signal_at) : null;
        const isOnline = lastSignal && (now.getTime() - lastSignal.getTime()) < onlineThreshold;

        if (isOnline) {
          online++;
          if (device.speed_kmh > 5) {
            moving++;
          } else {
            idle++;
          }
        } else {
          offline++;
        }
      });

      return {
        total: devices?.length || 0,
        online,
        offline,
        moving,
        idle,
      };
    },
    enabled: !!companyId,
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 20000,
  });
}
